/**
 * Shopify Storefront Cart API
 *
 * Low-level wrapper for Shopify's Cart GraphQL mutations.
 * All functions return a normalized cart object.
 */

const API_VERSION = "2024-10";

function getConfig() {
  const cfg = window.VANYX_CONFIG || {};
  return {
    domain: cfg.shopifyDomain || "shop.vanyx.io",
    token: cfg.shopifyToken || "",
  };
}

async function shopifyFetch(query, variables = {}) {
  const { domain, token } = getConfig();
  const lang = (window.VANYX_CONFIG || {}).lang || "de";
  const res = await fetch(
    `https://${domain}/api/${API_VERSION}/graphql.json`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": token,
        "Accept-Language": lang,
      },
      body: JSON.stringify({ query, variables }),
    },
  );

  if (!res.ok) {
    throw new Error(`Shopify API ${res.status}`);
  }

  const json = await res.json();
  if (json.errors) {
    throw new Error(json.errors[0].message);
  }
  return json.data;
}

const CART_FRAGMENT = `
  fragment CartFields on Cart {
    id
    checkoutUrl
    totalQuantity
    cost {
      totalAmount { amount currencyCode }
    }
    lines(first: 50) {
      nodes {
        id
        quantity
        merchandise {
          ... on ProductVariant {
            id
            title
            price { amount currencyCode }
            image { url altText }
            product { title handle }
          }
        }
        cost {
          totalAmount { amount currencyCode }
        }
      }
    }
  }
`;

function normalizeCart(cart) {
  return {
    id: cart.id,
    checkoutUrl: cart.checkoutUrl,
    totalQuantity: cart.totalQuantity,
    totalAmount: cart.cost.totalAmount.amount,
    currency: cart.cost.totalAmount.currencyCode,
    lines: cart.lines.nodes.map((line) => ({
      id: line.id,
      quantity: line.quantity,
      variantId: line.merchandise.id,
      title: line.merchandise.product.title,
      variantTitle:
        line.merchandise.title === "Default Title"
          ? ""
          : line.merchandise.title,
      price: line.merchandise.price.amount,
      lineTotal: line.cost.totalAmount.amount,
      currency: line.merchandise.price.currencyCode,
      image: line.merchandise.image?.url || "",
      imageAlt: line.merchandise.image?.altText || "",
      handle: line.merchandise.product.handle,
    })),
  };
}

export async function createCart(variantId, quantity = 1) {
  const data = await shopifyFetch(
    `mutation cartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart { ...CartFields }
        userErrors { field message }
      }
    }
    ${CART_FRAGMENT}`,
    {
      input: {
        lines: [{ merchandiseId: variantId, quantity }],
      },
    },
  );

  if (data.cartCreate.userErrors.length > 0) {
    throw new Error(data.cartCreate.userErrors[0].message);
  }
  return normalizeCart(data.cartCreate.cart);
}

export async function addToCart(cartId, variantId, quantity = 1) {
  const data = await shopifyFetch(
    `mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart { ...CartFields }
        userErrors { field message }
      }
    }
    ${CART_FRAGMENT}`,
    {
      cartId,
      lines: [{ merchandiseId: variantId, quantity }],
    },
  );

  if (data.cartLinesAdd.userErrors.length > 0) {
    throw new Error(data.cartLinesAdd.userErrors[0].message);
  }
  return normalizeCart(data.cartLinesAdd.cart);
}

export async function updateLine(cartId, lineId, quantity) {
  const data = await shopifyFetch(
    `mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart { ...CartFields }
        userErrors { field message }
      }
    }
    ${CART_FRAGMENT}`,
    {
      cartId,
      lines: [{ id: lineId, quantity }],
    },
  );

  if (data.cartLinesUpdate.userErrors.length > 0) {
    throw new Error(data.cartLinesUpdate.userErrors[0].message);
  }
  return normalizeCart(data.cartLinesUpdate.cart);
}

export async function removeLine(cartId, lineId) {
  const data = await shopifyFetch(
    `mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart { ...CartFields }
        userErrors { field message }
      }
    }
    ${CART_FRAGMENT}`,
    {
      cartId,
      lineIds: [lineId],
    },
  );

  if (data.cartLinesRemove.userErrors.length > 0) {
    throw new Error(data.cartLinesRemove.userErrors[0].message);
  }
  return normalizeCart(data.cartLinesRemove.cart);
}

export async function getProductByHandle(handle) {
  const data = await shopifyFetch(
    `query productByHandle($handle: String!) {
      productByHandle(handle: $handle) {
        variants(first: 100) {
          nodes {
            id
            availableForSale
            quantityAvailable
          }
        }
      }
    }`,
    { handle },
  );

  if (!data.productByHandle) {
    return null;
  }

  return data.productByHandle.variants.nodes.map((v) => ({
    id: v.id,
    available: v.availableForSale,
    quantity: v.quantityAvailable,
  }));
}

export async function getCart(cartId) {
  const data = await shopifyFetch(
    `query cart($cartId: ID!) {
      cart(id: $cartId) { ...CartFields }
    }
    ${CART_FRAGMENT}`,
    { cartId },
  );

  if (!data.cart) {
    return null;
  }
  return normalizeCart(data.cart);
}
